import { useEffect, useRef } from 'react';

import type { OrderableLicense, PlaceOrderInventory, RenewableOrder } from 'apis/PlaceOrderApi';
import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { GMetrixDatePicker } from 'components/GMetrixDatePicker/GMetrixDatePicker';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { GMetrixTable } from 'components/GMetrixTable/GMetrixTable';
import GMetrixToast from 'components/GMetrixToast/GMetrixToast';
import { usePlaceOrder } from 'pages/place_order/context';
import { FormattedMessage, useIntl } from 'react-intl';
import { helpContent } from 'pages/place_order/help';
import { PageHeader } from 'components/layout/page';
import { PlaceOrderApi } from 'apis/PlaceOrderApi';
import { Button, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

const LICENSE_TYPE_SITE = 1;
const LICENSE_TYPE_SEAT = 3;

const AUTOFILL_NO = 0;
const AUTOFILL_INVENTORY = 1;
const AUTOFILL_NEW_ORDER = 2;

const getDateString = (date: Date): string => {

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;

};

const today = getDateString(new Date());
const sixMonthsOut = getDateString(new Date(new Date().setMonth(new Date().getMonth() + 6)));

export const PlaceOrderWizard = () => {

    const [state, setState] = useSuperState({
        activeTab: 'new' as 'new' | 'renew',
        autoFill: AUTOFILL_NO,
        comment: '',
        inventory: [] as PlaceOrderInventory[],
        licenses: [] as OrderableLicense[],
        loading: false,
        numRequested: '' as string,
        placing: false,
        purchaseOrder: '',
        renewableOrders: [] as RenewableOrder[],
        renewControl: null as string | null,
        renewId: null as number | null,
        renewLicenseId: null as number | null,
        renewLicenseName: null as string | null,
        selectedLicense: null as OrderableLicense | null,
        startDate: today as string,
        step: 0,
        success: null as { done: boolean; message: string } | null
    });

    const { activeTab, autoFill, comment, inventory, licenses, loading, numRequested, placing, purchaseOrder, renewableOrders,
        renewControl, renewId, renewLicenseId, renewLicenseName, selectedLicense, startDate, step, success } = state;

    const { highestRole, selectedPartner } = useApp();
    const { permissions, requestingPartnerId } = usePlaceOrder();
    const { formatMessage } = useIntl();

    const toastRef = useRef<any>(null);

    const loadData = async () => {

        try {

            setState({ loading: true });

            const requests: Promise<any>[] = [
                PlaceOrderApi.GetOrderableLicenses(requestingPartnerId, selectedPartner.id)
            ];

            if (permissions.ShowAutofill) {

                requests.push(PlaceOrderApi.GetInventory(highestRole.partnerId));

            }

            if (permissions.ShowRenewTab) {

                requests.push(PlaceOrderApi.GetRenewableOrders(highestRole.partnerId, selectedPartner.id));

            }

            const results = await Promise.all(requests);

            let idx = 0;
            const licensesResult = results[idx++] ?? [];
            const inventoryResult = permissions.ShowAutofill ? (results[idx++] ?? []) : [];
            const renewableResult = permissions.ShowRenewTab ? (results[idx++] ?? []) : [];

            setState({
                inventory: inventoryResult,
                licenses: licensesResult,
                loading: false,
                renewableOrders: renewableResult
            });

        } catch (e: any) {

            setState({ loading: false });
            toastRef.current?.createToast(e?.message ?? (formatMessage({ id: 'failed_to_load_data.51299246', defaultMessage: 'Failed to load data.' })), 'danger');

        }

    };

    const getInventoryForLicense = (licenseId: number): number => {

        const match = inventory.find(i => i.LicenseID === licenseId);
        return match?.Inventory ?? 0;

    };

    const isSupplierSelf = (): boolean => {

        if (!selectedLicense) return false;
        return selectedLicense.SuppliedBy === highestRole.partnerId;

    };

    const hasAdequateInventory = (): boolean => {

        if (!selectedLicense) return false;
        const qty = parseInt(numRequested, 10);
        if (isNaN(qty) || qty <= 0) return false;
        return getInventoryForLicense(selectedLicense.ID) >= qty;

    };

    const isInventoryAutofillDisabled = (): boolean => !isSupplierSelf() || !hasAdequateInventory();

    const isNewOrderAutofillDisabled = (): boolean => !isSupplierSelf();

    const inventoryCount = selectedLicense ? getInventoryForLicense(selectedLicense.ID) : 0;

    const getDeliverableLabel = (type: number): string => {

        if (type === 1) return formatMessage({ id: 'practice_tests.f40d347b', defaultMessage: 'Practice Tests' });
        if (type === 2) return formatMessage({ id: 'ebooks.de78ae62', defaultMessage: 'eBooks' });
        if (type === 3) return formatMessage({ id: 'courses.c269a3fa', defaultMessage: 'Courses' });
        return formatMessage({ id: 'unknown.3fe48b83', defaultMessage: 'Unknown' });

    };

    const getUsesDisplay = (): string => {

        if (!selectedLicense?.Deliverables) return '';
        return selectedLicense.Deliverables
            .filter(d => d.NumUses != null)
            .map(d => `${getDeliverableLabel(d.Type)}: ${d.NumUses}`)
            .join(', ');

    };

    const getMaxUsesDisplay = (): string => {

        if (!selectedLicense?.Deliverables) return '';
        return selectedLicense.Deliverables
            .filter(d => d.MaxUses != null)
            .map(d => `${getDeliverableLabel(d.Type)}: ${d.MaxUses}`)
            .join(', ');

    };

    const getSeatsDisplay = (): string => {

        if (!selectedLicense?.Deliverables) return '';
        return selectedLicense.Deliverables
            .filter(d => d.NumSeats != null)
            .map(d => `${getDeliverableLabel(d.Type)}: ${d.NumSeats}`)
            .join(', ');

    };

    const getAutoFillLabel = (): string => {

        if (autoFill === AUTOFILL_INVENTORY) return formatMessage({ id: 'fill_from_inventory.c76e5908', defaultMessage: 'Fill From Inventory' });
        if (autoFill === AUTOFILL_NEW_ORDER) return formatMessage({ id: 'fill_with_new_order.aaeb1403', defaultMessage: 'Fill With New Order' });
        return formatMessage({ id: 'do_not_autofill.97df278f', defaultMessage: 'Do Not Autofill' });

    };

    const getLicenseTypeName = (): string => {

        if (!selectedLicense) return '';
        if (selectedLicense.LicenseType === LICENSE_TYPE_SITE) return formatMessage({ id: 'site.84ff8857', defaultMessage: 'Site' });
        if (selectedLicense.LicenseType === LICENSE_TYPE_SEAT) return formatMessage({ id: 'seat.42016b6c', defaultMessage: 'Seat' });
        return formatMessage({ id: 'individual.03bcdacf', defaultMessage: 'Individual' });

    };

    const handleRenew = (order: RenewableOrder) => {

        // Match old page: try to find full license in orderable list for confirmation details,
        // but always store the renewal's license ID separately (like the old page's hidden field)
        const license = licenses.find(l => l.ID === order.LicenseId)
            ?? { ID: order.LicenseId, Name: order.LicenseName } as OrderableLicense;

        setState({
            activeTab: 'new',
            numRequested: String(order.NumRequested),
            renewControl: order.OrderControl,
            renewId: order.ID,
            renewLicenseId: order.LicenseId,
            renewLicenseName: order.LicenseName,
            selectedLicense: license,
            startDate: order.DateExpire ? getDateString(new Date(order.DateExpire)) : ''
        });

    };

    const handleResetRenew = () => {

        setState({
            numRequested: '',
            renewControl: null,
            renewId: null,
            renewLicenseId: null,
            renewLicenseName: null,
            selectedLicense: null
        });

    };

    const validateStep1 = (): boolean => {

        // Renewal has license and quantity pre-filled and locked — skip those checks
        if (!renewId) {

            if (!selectedLicense || selectedLicense.ID === 0) {

                toastRef.current?.createToast(formatMessage({ id: 'please_select_a_license_to_continue.caa100d5', defaultMessage: 'Please select a license to continue.' }), 'danger');
                return false;

            }

            const qty = parseInt(numRequested, 10);
            if (isNaN(qty) || qty <= 0) {

                toastRef.current?.createToast(formatMessage({ id: 'please_provide_a_quantity_greater_than_0.771145af', defaultMessage: 'Please provide a quantity greater than 0.' }), 'danger');
                return false;

            }

        }

        if (!startDate) {

            toastRef.current?.createToast(formatMessage({ id: 'please_select_a_start_date.94637100', defaultMessage: 'Please select a start date.' }), 'danger');
            return false;

        }

        return true;

    };

    const handleNext = () => {

        if (step === 0) {

            if (!validateStep1()) return;

            if (autoFill === AUTOFILL_INVENTORY && !hasAdequateInventory()) {

                setState({ autoFill: AUTOFILL_NO });
                toastRef.current?.createToast(
                    formatMessage({ id: 'you_do_not_have_adaquate_inventory_to_au.8f51ffc3', defaultMessage: 'You do not have adaquate inventory to AutoFill this order. The order can still be placed and filled at a later time.' }),
                    'warning'
                );

            }

            setState({ step: 1 });

        } else if (step === 1) {

            handlePlaceOrder();

        }

    };

    const handleBack = () => {

        if (step === 1) {

            setState({ step: 0 });

        }

    };

    const handlePlaceOrder = async () => {

        // For renewals, use the stored renewal license ID (like old page's hidden field)
        // For new orders, use the selected license from dropdown
        const licenseId = renewId ? renewLicenseId ? renewLicenseId : 0 : selectedLicense?.ID ? selectedLicense.ID : 0;

        try {

            setState({ placing: true });

            const result = await PlaceOrderApi.PlaceOrder({
                comment,
                futureStart: startDate || null,
                isAutofill: autoFill,
                isOffline: false,
                licenseId: licenseId,
                numRequested: parseInt(numRequested, 10),
                orderIdToAutofill: null,
                partnerForId: selectedPartner.id,
                purchaseOrder,
                renewId: renewId,
                requestingPartner: highestRole.partnerId
            });

            setState({
                placing: false,
                step: 2,
                success: { done: result.Done, message: result.Message }
            });

        } catch (e: any) {

            setState({ placing: false });
            toastRef.current?.createToast(e?.message ?? (formatMessage({ id: 'failed_to_place_order.b0bc7650', defaultMessage: 'Failed to place order.' })), 'danger');

        }

    };

    const handleGoHome = () => {

        window.location.href = '/';

    };

    useEffect(() => {

        if (autoFill === AUTOFILL_INVENTORY && isInventoryAutofillDisabled()) {

            setState({ autoFill: AUTOFILL_NO });

        }
        if (autoFill === AUTOFILL_NEW_ORDER && isNewOrderAutofillDisabled()) {

            setState({ autoFill: AUTOFILL_NO });

        }

    }, [selectedLicense?.ID, numRequested]);

    useEffect(() => {

        loadData();

    }, []);

    const renewableColumns = [
        { field: 'LicenseName' as const, title: formatMessage({ id: 'license.2240261c', defaultMessage: 'License', description: 'Formulario' }) },
        {
            cell: (cellProps: any) => (
                <td>
                    {cellProps.dataItem.DateCreate ? new Date(cellProps.dataItem.DateCreate).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                </td>
            ),
            field: 'DateCreate' as const,
            title: formatMessage({ id: 'order_date.82144431', defaultMessage: 'Order Date', description: 'Gridview header' })
        },
        {
            cell: (cellProps: any) => (
                <td>
                    {cellProps.dataItem.DateExpire ? new Date(cellProps.dataItem.DateExpire).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                </td>
            ),
            field: 'DateExpire' as const,
            title: formatMessage({ id: 'expiration_date.badff624', defaultMessage: 'Expiration Date', description: 'Gridview header' })
        },
        { field: 'RequestedUserFullName' as const, title: formatMessage({ id: 'ordered_by.381902ec', defaultMessage: 'Ordered By', description: 'Formulario' }) },
        { field: 'NumAvailable' as const, title: formatMessage({ id: 'available.d321561a', defaultMessage: 'Available' }), width: 100 },
        {
            cell: (cellProps: any) => {

                const order = cellProps.dataItem as RenewableOrder;
                const isRenewed = order.RenewedByID != null;
                return (
                    <td>
                        <GMetrixButton
                            disabled={isRenewed}
                            onClick={() => handleRenew(order)}
                            variant={isRenewed ? 'secondary' : 'primary'}
                        >
                            {<FormattedMessage defaultMessage="Renew" id="renew.5c5e1a25" />}
                        </GMetrixButton>
                    </td>
                );

            },
            field: 'ID' as const,
            title: formatMessage({ id: 'renew.5c5e1a25', defaultMessage: 'Renew' }),
            width: 100
        }
    ];

    const licenseDropdownItems = (() => {

        const items = [{ ID: 0, Name: formatMessage({ id: 'choose_one.e57d2911', defaultMessage: 'Choose one', description: 'formulario' }) } as OrderableLicense, ...licenses];

        // For renewals: if the renewal license isn't in the orderable list, add it so the dropdown can display it
        if (renewLicenseId && !licenses.some(l => l.ID === renewLicenseId)) {

            items.push({ ID: renewLicenseId, Name: renewLicenseName ?? '' } as OrderableLicense);

        }

        return items;

    })();

    return (
        <div>
            <GMetrixToast ref={toastRef} />

            {step === 0 && (
                <div>

                    <PageHeader
                        title={(
                            <span className={'h2 d-inline-flex align-items-center gap-2 flex-wrap'}>
                                <span>
                                    <FormattedMessage defaultMessage="Placing a New Order for:" id="placing_a_new_order_for.70e1c6c3" />
                                </span>
                                <span className={'text-muted'}>
                                    {selectedPartner.name}
                                </span>
                            </span>
                        )}
                        helpContent={helpContent}
                    />

                    {loading ?
                        (
                            <div className="text-center p-5">
                                <GMetrixLoader size="large" />
                            </div>
                        )
                        :
                        (
                            <Card>
                                <Card.Body>
                                    <div className="row g-3 mb-4">
                                        <div className="col-12 col-md-6 col-lg-3">
                                            <GMetrixInput
                                                label={formatMessage({ id: 'purchase_order_optional.38e6ecd9', defaultMessage: 'Purchase Order (Optional)' })}
                                                maxLength={50}
                                                onChange={e => setState({ purchaseOrder: e.target.value })}
                                                type="text"
                                                value={purchaseOrder}
                                            />
                                        </div>
                                        <div className="col-12 col-md-6 col-lg-3">
                                            <GMetrixDatePicker
                                                label={formatMessage({ id: 'start_date.66ba5b72', defaultMessage: 'Start Date' })}
                                                max={sixMonthsOut}
                                                min={today}
                                                onChange={val => setState({ startDate: val ? getDateString(val) : '' })}
                                                required={true}
                                                value={startDate}
                                            />
                                        </div>
                                        {permissions.ShowComment && (
                                        <div className="col-12 col-md-6 col-lg-3">
                                            <GMetrixInput
                                                label={formatMessage({ id: 'comment.252dd7a3', defaultMessage: 'Comment', description: 'Formulario' })}
                                                maxLength={1000}
                                                onChange={e => setState({ comment: e.target.value })}
                                                rows={3}
                                                type="text"
                                                value={comment}
                                            />
                                        </div>
                                        )}
                                        {permissions.ShowAutofill && (
                                        <div className="col-12 col-md-6 col-lg-3">
                                            <div className="border rounded p-3 h-100 bg-light">
                                                <p className="fw-bold mb-2">
                                                    {<FormattedMessage defaultMessage="Autofill Options" id="autofill_options.a4d4bd6d" />}
                                                </p>
                                                <GMetrixCheckInput
                                                    checked={autoFill === AUTOFILL_NO}
                                                    label={formatMessage({ id: 'do_not_autofill.97df278f', defaultMessage: 'Do Not Autofill' })}
                                                    name="autoFill"
                                                    onChange={() => setState({ autoFill: AUTOFILL_NO })}
                                                    type="radio"
                                                />
                                                <GMetrixCheckInput
                                                    checked={autoFill === AUTOFILL_INVENTORY}
                                                    disabled={isInventoryAutofillDisabled()}
                                                    label={formatMessage({ id: 'fill_from_inventory_count_available.a30efc21', defaultMessage: 'Fill From Inventory ({count} Available)' }, { count: inventoryCount })}
                                                    name="autoFill"
                                                    onChange={() => setState({ autoFill: AUTOFILL_INVENTORY })}
                                                    type="radio"
                                                />
                                                <GMetrixCheckInput
                                                    checked={autoFill === AUTOFILL_NEW_ORDER}
                                                    disabled={isNewOrderAutofillDisabled()}
                                                    label={formatMessage({ id: 'fill_with_new_order.aaeb1403', defaultMessage: 'Fill With New Order' })}
                                                    name="autoFill"
                                                    onChange={() => setState({ autoFill: AUTOFILL_NEW_ORDER })}
                                                    type="radio"
                                                />
                                            </div>
                                        </div>
                                        )}
                                    </div>

                                    {renewId && (
                                    <div className="alert alert-info d-flex align-items-center justify-content-between mb-3">
                                        <span>
                                            {<FormattedMessage defaultMessage="Extending Order For:" id="extending_order_for.05014ee8" />}
                                            <strong>
                                                {renewControl}
                                            </strong>
                                        </span>
                                        <GMetrixButton onClick={handleResetRenew} variant="outline-primary">
                                            <FormattedMessage defaultMessage="Cancel Renewal" id="cancel_renewal.51320b49" />
                                        </GMetrixButton>
                                    </div>
                                    )}

                                    <ul className="nav nav-tabs">

                                        <li className="nav-item">
                                            <a
                                                onClick={e => {

                                                    e.preventDefault();
                                                    setState({ activeTab: 'new' });

                                                }}
                                                className={`nav-link ${activeTab === 'new' ? 'active' : ''}`}
                                                href="#"
                                            >
                                                {<FormattedMessage defaultMessage="Place Order" id="place_order.0da32b8f" />}
                                            </a>
                                        </li>

                                        {permissions.ShowRenewTab && (
                                            <li className="nav-item">
                                                <a
                                                    onClick={e => {

                                                        e.preventDefault();
                                                        setState({ activeTab: 'renew' });

                                                    }}
                                                    className={`nav-link ${activeTab === 'renew' ? 'active' : ''}`}
                                                    href="#"
                                                >
                                                    {<FormattedMessage defaultMessage="Renew Existing Order" id="renew_existing_order.d7f4094c" />}
                                                </a>
                                            </li>
                                        )}

                                    </ul>

                                    <div className="border border-top-0 rounded-bottom p-3">

                                        {activeTab === 'new' && (
                                            <div className="row g-3">
                                                <div className="col-12 col-sm-6 col-md-4">
                                                    <GMetrixDropdown
                                                        limitMenuWidth
                                                        onChange={val => {

                                                            if (renewId) return;
                                                            const selected = Array.isArray(val) ? val[0] : val;
                                                            setState({
                                                                autoFill: AUTOFILL_NO,
                                                                selectedLicense: selected.ID === 0 ? null : selected
                                                            });

                                                        }}
                                                        dataItemKey="ID"
                                                        dataItems={licenseDropdownItems}
                                                        disabled={renewId != null}
                                                        label={formatMessage({ id: 'license_type.3286e892', defaultMessage: 'License Type', description: 'Formulario' })}
                                                        textField="Name"
                                                        value={selectedLicense ?? licenseDropdownItems[0]}
                                                    />
                                                </div>
                                                <div className="col-12 col-sm-6 col-md-3">
                                                    <GMetrixInput
                                                        onChange={e => {

                                                            const val = e.target.value;
                                                            const num = parseInt(val, 10);
                                                            setState({ numRequested: !isNaN(num) && num <= 0 ? '1' : val });

                                                        }}
                                                        disabled={renewId != null}
                                                        label={formatMessage({ id: 'quantity.3c513ef2', defaultMessage: 'Quantity' })}
                                                        min={1}
                                                        required={true}
                                                        type="number"
                                                        value={numRequested}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {activeTab === 'renew' && permissions.ShowRenewTab && (
                                            <div>
                                                <GMetrixTable
                                                    columnData={renewableColumns}
                                                    rowData={renewableOrders.map(o => ({ ...o, Id: o.ID }))}
                                                />
                                            </div>
                                        )}

                                    </div>

                                    <div className={'d-flex justify-content-end mt-3'}>
                                        <GMetrixButton
                                            disabled={loading}
                                            onClick={handleNext}
                                            variant="primary"
                                        >
                                            {<FormattedMessage defaultMessage="Next" description="botones" id="next.b637640c" />}
                                        </GMetrixButton>
                                    </div>
                                </Card.Body>
                            </Card>
                        )
                    }
                </div>
            )}

            {step === 1 && (
                <div>

                    <PageHeader helpContent={helpContent} title={formatMessage({ id: 'order_details.f1808d77', defaultMessage: 'Order Details' })}/>

                    <Card>
                        <Card.Body>

                            <ul className="list-group">
                                <li className="list-group-item d-flex justify-content-between align-items-center">
                                    <strong>
                                        <FormattedMessage defaultMessage="Order For" id="order_for.5bdac16b" />
                                        :
                                    </strong>
                                    {' '}
                                    {selectedPartner.name}
                                </li>
                                <li className="list-group-item d-flex justify-content-between align-items-center">
                                    <strong>
                                        {<FormattedMessage defaultMessage="Start Date" id="start_date.66ba5b72" />}
                                        :
                                    </strong>
                                    {' '}
                                    {startDate}
                                </li>
                                <li className="list-group-item d-flex justify-content-between align-items-center">
                                    <strong>
                                        {<FormattedMessage defaultMessage="Quantity" id="quantity.3c513ef2" />}
                                        :
                                    </strong>
                                    {' '}
                                    {numRequested}
                                </li>
                                {permissions.ShowAutofill && (
                                    <li className="list-group-item d-flex justify-content-between align-items-center">
                                        <strong>
                                            {<FormattedMessage defaultMessage="Autofill Options" id="autofill_options.a4d4bd6d" />}
                                            :
                                        </strong>
                                        {' '}
                                        {getAutoFillLabel()}
                                    </li>
                                )}
                                <li className="list-group-item d-flex justify-content-between align-items-center">
                                    <strong>
                                        {<FormattedMessage defaultMessage="Purchase Order" id="purchase_order.17844493" />}
                                        :
                                    </strong>
                                    {' '}
                                    {purchaseOrder || <FormattedMessage defaultMessage="None" id="none.a51ad9ee" />}
                                </li>
                                {permissions.ShowComment && (
                                    <li className="list-group-item d-flex justify-content-between align-items-center">
                                        <strong>
                                            {<FormattedMessage defaultMessage="Comment" description="Formulario" id="comment.252dd7a3" />}
                                            :
                                        </strong>
                                        {' '}
                                        {comment || <FormattedMessage defaultMessage="None" id="none.a51ad9ee" />}
                                    </li>
                                )}
                                <li className="list-group-item d-flex justify-content-between align-items-center">
                                    <strong>
                                        {<FormattedMessage defaultMessage="License Name" id="license_name.8773f362" />}
                                        :
                                    </strong>
                                    {' '}
                                    {selectedLicense?.Name ?? renewLicenseName ?? ''}
                                </li>
                                <li className="list-group-item d-flex justify-content-between align-items-center">
                                    <strong>
                                        {<FormattedMessage defaultMessage="License Type" description="Formulario" id="license_type.3286e892" />}
                                        :
                                    </strong>
                                    {' '}
                                    {getLicenseTypeName()}
                                </li>
                                <li className="list-group-item d-flex justify-content-between align-items-center">
                                    <strong>
                                        <FormattedMessage defaultMessage="Days to Expire" id="days_to_expire.d742b9c6" />
                                        :
                                    </strong>
                                    {' '}
                                    {selectedLicense?.DaysExpire ?? ''}
                                </li>
                                <li className="list-group-item d-flex justify-content-between align-items-center">
                                    <strong>
                                        {<FormattedMessage defaultMessage="Usage Days" description="Formulario" id="usage_days.d9750693" />}
                                        :
                                    </strong>
                                    {' '}
                                    {selectedLicense?.UsageDays ?? ''}
                                </li>
                                {selectedLicense?.LicenseType === LICENSE_TYPE_SITE && getUsesDisplay() && (
                                    <li className="list-group-item d-flex justify-content-between align-items-center">
                                        <strong>
                                            <FormattedMessage defaultMessage="Number of Uses" id="number_of_uses.477731c6" />
                                            :
                                        </strong>
                                        {' '}
                                        {getMaxUsesDisplay() || getUsesDisplay()}
                                    </li>
                                )}
                                {selectedLicense?.LicenseType === LICENSE_TYPE_SEAT && getSeatsDisplay() && (
                                    <li className="list-group-item d-flex justify-content-between align-items-center">
                                        <strong>
                                            <FormattedMessage defaultMessage="Number of Seats" id="number_of_seats.91e508e7" />
                                            :
                                        </strong>
                                        {' '}
                                        {getSeatsDisplay()}
                                    </li>
                                )}
                                {getMaxUsesDisplay() && (
                                    <li className="list-group-item d-flex justify-content-between align-items-center">
                                        <strong>
                                            {<FormattedMessage defaultMessage="Max Uses" id="max_uses.5571019d" />}
                                            :
                                        </strong>
                                        {' '}
                                        {getMaxUsesDisplay()}
                                    </li>
                                )}
                                {renewControl && (
                                    <li className="list-group-item d-flex justify-content-between align-items-center">
                                        <strong>
                                            <FormattedMessage defaultMessage="Renewing Order" id="renewing_order.d72a46e5" />
                                            :
                                        </strong>
                                        {' '}
                                        {renewControl}
                                    </li>
                                )}
                            </ul>

                            <div className="d-flex justify-content-end gap-1 mt-3">
                                <GMetrixButton
                                    disabled={placing}
                                    onClick={handleBack}
                                    variant="outline-primary"
                                >
                                    {<FormattedMessage defaultMessage="Back" description="botones" id="back.2bdafbc4" />}
                                </GMetrixButton>
                                <GMetrixButton
                                    disabled={placing}
                                    onClick={handleNext}
                                    variant="primary"
                                >
                                    {placing ? <FormattedMessage defaultMessage="Placing..." id="placing.ca5a7e88" /> : <FormattedMessage defaultMessage="Place Order" id="place_order.0da32b8f" />}
                                </GMetrixButton>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            )}

            {step === 2 && (
                <div>

                    <PageHeader helpContent={helpContent} title={formatMessage({ id: 'placed_orders.cd0237b2', defaultMessage: 'Placed Orders' })}/>

                    <Card>
                        <Card.Body>
                            {success?.done ?
                                (
                                    <div className="alert alert-success">
                                        {<FormattedMessage defaultMessage="The Order was submitted successfully." description="Mensaje" id="the_order_was_submitted_successfully.64ec5891" />}
                                    </div>
                                )
                                :
                                (
                                    <div className="alert alert-danger">

                                        {<FormattedMessage defaultMessage="There was an error placing this order" id="there_was_an_error_placing_this_order.060e7163" />}

                                        {success?.message && (
                                            <div className="mt-2">
                                                <small>
                                                    {success.message}
                                                </small>
                                            </div>
                                        )}
                                    </div>
                                )}

                            <div className={'d-flex justify-content-end mt-3'}>
                                <Button as={Link} to={'/'}>
                                    <FormattedMessage defaultMessage="Return Home" description="botones" id="return_home.068b78a0" />
                                </Button>
                            </div>

                        </Card.Body>
                    </Card>

                </div>
            )}
        </div>
    );

};
